import { useCallback, useEffect, useState } from "react";
import { Play, Square, Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../i18n/LangContext";
import { api, downloadInvoice, fmtDate, fmtDuration, fmtEur } from "../../lib/api";

const IV_COLOR = { assigned: "var(--amber)", running: "var(--cyan)", completed: "#6FCF97" };

export const IvStatus = ({ status }) => {
  const { t } = useLang();
  return <span className="pill" style={{ "--c": IV_COLOR[status], "--ct": IV_COLOR[status] }}>{t.dossier.statusIv[status]}</span>;
};

export const InvoiceButton = ({ iv }) => {
  const { t, lang } = useLang();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await downloadInvoice(iv.id, iv.ref, lang); } catch { toast.error(t.errors.generic); }
    setBusy(false);
  };
  return (
    <button type="button" className="icon-link link-btn" onClick={run} disabled={busy} aria-label={`${t.dossier.invoice} ${iv.ref}`} data-testid={`invoice-btn-${iv.ref}`}>
      {busy ? <Loader2 className="spin" size={14} aria-hidden="true" /> : <FileDown aria-hidden="true" />}PDF
    </button>
  );
};

const useTick = (active) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
};

const Mission = ({ iv, onChange }) => {
  const { t, lang } = useLang();
  const d = t.dossier;
  const [busy, setBusy] = useState(false);
  const now = useTick(iv.status === "running");
  const elapsed = iv.status === "running" ? (now - new Date(iv.started_at).getTime()) / 1000 : iv.duration_seconds;
  const cost = iv.status === "running" ? (iv.hourly_rate * elapsed) / 3600 : iv.amount_ht;

  const act = async (action) => {
    setBusy(true);
    try { await api.post(`/interventions/${iv.id}/${action}`); await onChange(); } catch { toast.error(t.errors.generic); }
    setBusy(false);
  };

  return (
    <div className={`mission${iv.status === "running" ? " running" : ""}`} data-testid={`mission-${iv.ref}`}>
      <div className="mission-info">
        <div className="mission-top"><span className="mono strong">{iv.ref}</span><IvStatus status={iv.status} /></div>
        <b>{iv.client_company}</b>
        <div className="match-meta">{d.request} {iv.diagnostic_ref || "—"} · {iv.site || t.pro.timer.siteNA} · {fmtDate(iv.assigned_at, lang)} · {fmtEur(iv.hourly_rate, lang)} / h</div>
      </div>
      <div className="mission-timer">
        <span className="timer-mini" data-testid={`mission-timer-${iv.ref}`}>{fmtDuration(elapsed || 0)}</span>
        <span className="timer-cost">{fmtEur(cost, lang)} {t.pro.timer.engaged}</span>
      </div>
      <div className="mission-actions">
        {iv.status === "assigned" && <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => act("start")} data-testid={`mission-start-btn-${iv.ref}`}><Play aria-hidden="true" />{d.start}</button>}
        {iv.status === "running" && <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => act("stop")} data-testid={`mission-stop-btn-${iv.ref}`}><Square aria-hidden="true" />{d.stop}</button>}
        {iv.status === "completed" && <InvoiceButton iv={iv} />}
      </div>
    </div>
  );
};

export const ExpertMissions = () => {
  const { t } = useLang();
  const d = t.dossier;
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => setRows((await api.get("/me/interventions")).data), []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="panel timer-panel rivets-bottom" style={{ textAlign: "left" }} data-testid="expert-missions-panel">
      <span className="rv" aria-hidden="true" />
      <span className="console-title">{t.pro.timer.title}</span>
      <h3 className="sub-title" style={{ marginTop: 14 }}>{d.myMissions}</h3>
      <p className="timer-note" style={{ marginLeft: 0, textAlign: "left" }}>{t.pro.timer.note}</p>
      {rows === null ? <Loader2 className="spin" aria-label={t.common.loading} /> : rows.length === 0
        ? <p className="empty" data-testid="missions-empty">{d.noMissions}</p>
        : <div className="missions">{rows.map((iv) => <Mission key={iv.id} iv={iv} onChange={load} />)}</div>}
    </div>
  );
};
